/**
 * 회원 검색어 판정 — 서버로 보낼 수 있는 입력인지 로컬에서 먼저 가른다.
 *
 * BE `search` 는 `name` / `user_id` 부분일치만 필터한다 (연락처·이메일은 대상이 아니다).
 * 그런데 연락처는 단순히 "결과가 안 나오는" 문제가 아니다 — 검색어는 `GET ?search=` 로
 * 나가므로 access log·브라우저 히스토리·프록시에 **원문 전화번호가 그대로 남는다**.
 * 목록에서 연락처 원문을 없애는 것이 목적인 변경에서 그 경로를 열어두면 역행이다.
 *
 * 그래서 전화번호처럼 보이는 입력은 **요청 자체를 만들지 않고** 로컬에서 안내한다.
 *
 * ## 판정 원칙
 * 1. **NFKC 먼저.** 전각 숫자·전각 하이픈 같은 호환 문자를 ASCII 로 접는다.
 *    BE `normalize_phone` 이 쓰는 규약과 같아 앞뒤가 어긋나지 않는다.
 * 2. **구분자를 넘어 숫자를 잇는다.** 유니코드 대시류·`/`·`.`·`_`·공백·괄호는 전화번호
 *    표기에 흔히 섞인다 (Word 자동교정, 메신저 복붙, IME).
 * 3. **문장 속 부분 문자열도 본다.** `tel:010-...`·`연락처 010-...` 처럼 앞뒤에 글자가
 *    붙어도 번호는 번호다.
 * 4. **자릿수가 아니라 구조로 판정한다.** "숫자 7자 이상"으로 잡으면 숫자형 `user_id`
 *    (`12345678`)까지 막힌다. 한국 휴대폰 접두·지역번호·국가번호 구조에 맞을 때만 막는다.
 */

/** 후보 목록 상한 — BE 에 `page_size` 로 그대로 전달한다. */
export const MEMBER_SEARCH_LIMIT = 20;

/** 이보다 짧으면 요청하지 않는다 — 한 글자 검색은 사실상 전량 조회다. */
export const MEMBER_SEARCH_MIN_LENGTH = 2;

/**
 * 검색어 최대 길이(코드포인트). BE `user_id` 는 40자, `name` 도 이보다 짧다 — 100자를
 * 넘는 입력은 검색어가 아니라 **붙여넣기 사고**다.
 *
 * 상한을 두는 이유는 길이 자체보다 **동기 전화 분석 비용**이다. 1M 자 붙여넣기가 그대로
 * 분석에 들어가면 UI 가 멈춘다. 판정 맨 앞에서 끊어 분석을 아예 시작하지 않는다.
 */
export const MEMBER_SEARCH_MAX_INPUT_LENGTH = 100;

/**
 * 코드포인트 수가 상한을 넘는가. **전체를 세지 않는다** — 상한+1 에서 멈추므로
 * 1M 자 입력도 101 코드포인트만 훑고 끝난다.
 */
/**
 * 원문에 허용하는 **절대 상한**. 보이는 글자 수(`MEMBER_SEARCH_MAX_INPUT_LENGTH`)의 4배 +1 —
 * NFD 결합열(한글 음절 = 3 codepoint)과 서식 문자가 섞여도 100 글자를 담을 여유가 있고,
 * 그 이상은 어떤 정규화를 거쳐도 상한 안에 들어올 수 없다. 이 값이 **모든 순회의 상한**이라
 * 1M 자 붙여넣기에도 401 codepoint 만 훑는다.
 */
export const MEMBER_SEARCH_HARD_MAX_INPUT_LENGTH = MEMBER_SEARCH_MAX_INPUT_LENGTH * 4 + 1;

/** 앞에서부터 최대 `limit` codepoint 까지 세고, 도달한 지점의 code unit 인덱스를 함께 준다. */
const scanCodePoints = (value: string, limit: number): { count: number; end: number } => {
  let count = 0;
  let i = 0;
  while (i < value.length && count < limit) {
    const code = value.charCodeAt(i);
    const wide = code >= 0xd800 && code <= 0xdbff && i + 1 < value.length;
    i += wide ? 2 : 1;
    count += 1;
  }
  return { count, end: i };
};

/**
 * 입력값을 **절대 상한까지만** 남긴다.
 *
 * HTML `maxLength` 는 붙여넣기를 UTF-16 코드 단위로 잘라 astral 문자를 반으로 쪼갠다.
 * 여기서는 codepoint 경계에서만 자르고, **401번째에서 멈춘다** — 1M 자를 붙여넣어도
 * 전체를 훑지 않는다. 잘라 낸 값을 state 에 넣으면 이후 모든 판정·렌더가 O(401) 이 된다.
 */
export const boundMemberSearchInput = (value: string): string => {
  const raw = value ?? "";
  const { count, end } = scanCodePoints(raw, MEMBER_SEARCH_HARD_MAX_INPUT_LENGTH);
  return count < MEMBER_SEARCH_HARD_MAX_INPUT_LENGTH ? raw : raw.slice(0, end);
};

/**
 * 상한을 넘었는가 — **보이는 글자 수** 기준이다.
 *
 * 원문 codepoint 로만 세면 NFD 결합열(`각` = 3 codepoint)과 서식 문자가 길이를 부풀려,
 * 사람이 34자만 친 입력이 `too-long` 으로 막힌다. 두 단계로 본다:
 *
 *  1. 원문이 절대 상한 이상이면 분석 없이 끊는다 (긴 붙여넣기 방어 — O(401)).
 *  2. 그 안쪽 값만 NFC + 서식 문자 제거를 거쳐 **보이는 글자**를 센다.
 */
const exceedsMaxLength = (value: string): boolean => {
  const raw = value ?? "";
  const { count } = scanCodePoints(raw, MEMBER_SEARCH_HARD_MAX_INPUT_LENGTH);
  if (count >= MEMBER_SEARCH_HARD_MAX_INPUT_LENGTH) return true;
  return codePointLength(analysisCopy(raw)) > MEMBER_SEARCH_MAX_INPUT_LENGTH;
};

/**
 * 한국 전화번호 후보 패턴. 각 항목은 **구분자를 제거한 숫자열** 기준이다.
 *
 * 구분자를 열거해 "이어붙일 문자"를 정하는 방식은 목록에 없는 문자(쉼표·콜론·파이프·
 * 가운뎃점·zero-width)에서 그대로 뚫린다. 대신 **숫자 그룹의 연속 부분 체인**을 후보
 * window 로 훑으므로 사이에 무엇이 있든 상관없다.
 */
const KR_PHONE_PATTERNS = [
  /^01[016789][0-9]{7,8}$/, // 휴대폰 010·011·016·017·018·019
  /^02[0-9]{7,8}$/, // 서울
  /^0(?:3[1-3]|4[1-4]|5[1-5]|6[1-4])[0-9]{7,8}$/, // 지역번호 031~064
  /^070[0-9]{7,8}$/, // 인터넷전화
  /^050[0-9][0-9]{7,8}$/, // 050x 안심번호
  /^060[0-9]{7,8}$/, // 060 전화정보서비스(유료) — 지역번호 06[1-4] 와 별개
  /^080[0-9]{7,8}$/, // 수신자부담
  /^1[568][0-9]{6}$/, // 전국대표 15xx·16xx·18xx (8자리)
];

/**
 * 한국 전화번호로 **자라날 수 있는** 접두. 완성형은 자기 자신의 접두이므로 이 목록이
 * `KR_PHONE_PATTERNS` 를 포함한다.
 *
 * 자동검색 화면은 300ms 디바운스로 요청을 만든다. 사람이 번호를 국번 단위로 끊어 입력하고
 * 그 사이에 멈추면 **완성 전 조각**이 그대로 `?search=` 로 나간다 (실측: `"010-1234-"`).
 * 부분 번호도 같은 성질의 PII 이므로 완성형만 막아서는 통제가 성립하지 않는다.
 */
const KR_PHONE_PREFIX_PATTERNS = [
  /^01[016789][0-9]{0,8}$/, // 휴대폰
  /^02[0-9]{0,8}$/, // 서울
  /^0(?:3[1-3]|4[1-4]|5[1-5]|6[1-4])[0-9]{0,8}$/, // 지역번호
  /^070[0-9]{0,8}$/, // 인터넷전화
  /^050[0-9]{0,9}$/, // 050x 안심번호
  /^060[0-9]{0,8}$/, // 060 전화정보서비스(유료)
  /^080[0-9]{0,8}$/, // 수신자부담
  /^1[568][0-9]{0,6}$/, // 전국대표
];

/**
 * 접두 판정을 적용하는 최소 숫자 자릿수.
 *
 * 2자리(`01`·`02`)까지 막으면 `김철수02` 같은 **이름+숫자**가 함께 막힌다. 2자리 자체는
 * 식별 정보가 아니므로 3자리를 경계로 둔다 — 실제 번호 조각은 3자리부터 시작한다.
 */
const PREFIX_MIN_DIGITS = 3;

/** 국제표기 최대 자릿수: `0082` + 국내 11자리. 후보는 이보다 길 수 없다. */
const MAX_PHONE_CANDIDATE_DIGITS = 15;
/** 가입자 번호 최소 유효 길이 — 뒤 잡음을 잘라도 이만큼은 남겨야 후보다. */
const MIN_SUBSCRIBER_DIGITS = 4;

const KR_COUNTRY_CODE = "82";

/**
 * **보이지 않는** 서식 문자 (Unicode General_Category = Cf).
 *
 * `\p{Cf}` 는 정규식 `u` 플래그가 필요한데 이 프로젝트의 tsconfig target 은 `es5` 라
 * 빌드가 깨진다 (TS1501). tsconfig 를 건드리지 않기 위해 BMP 범위를 명시한다 —
 * soft hyphen · ZWSP/ZWNJ/ZWJ · bidi 제어 · word joiner · BOM · interlinear annotation.
 */
/**
 * 스크립트별 `0` 코드포인트 **전량**. NFKC 는 호환 등가만 접으므로 Arabic-Indic `٠` ·
 * Adlam `𞥐` 같은 다른 스크립트의 숫자는 그대로 남고, 그러면 숫자 그룹이 하나도 잡히지
 * 않아 전화 판정을 통째로 빠져나간다.
 *
 * 목록은 **unicode.org 공식 UCD** 로 생성했다 — 로컬 `unicodedata` 는 파이썬 버전에 묶여
 * Unicode 15/16/17 신규 스크립트(Kawi·Nag Mundari·Sunuwar·Kirat Rai 등)를 놓친다.
 *
 *   기준: UnicodeData.txt · Unicode **17.0.0** (2025-08-15)
 *   출처: https://www.unicode.org/Public/UCD/latest/ucd/UnicodeData.txt
 *   sha256: 2e1efc1dcb59c575eedf5ccae60f95229f706ee6d031835247d843c11d96470c
 *   조건: field3(category) == "Nd" && field6(decimal) == "0" 이고
 *         뒤이은 9개 코드포인트가 Nd 이면서 decimal 1..9 로 연속
 *   결과: **77개 블록** (그중 astral 40개)
 *
 * 재생성 절차와 대조 결과는 `.harness/artifacts/.../ucd-nd-verification.md` 에 있다.
 * raw UCD 파일은 커밋하지 않는다 — 위 URL 에서 다시 받으면 된다.
 *
 * 정규식 `\p{Nd}` 를 쓰지 않는 이유: tsconfig target 이 `es5` 라 `u` 플래그가 필요해 빌드가
 * 깨진다. 표로 명시하면 지원 범위가 코드에 남아 검증도 쉽다.
 */
const DECIMAL_ZEROS = [
  0x0030, // DIGIT ZERO
  0x0660, // ARABIC-INDIC
  0x06f0, // EXTENDED ARABIC-INDIC
  0x07c0, // NKO
  0x0966, // DEVANAGARI
  0x09e6, // BENGALI
  0x0a66, // GURMUKHI
  0x0ae6, // GUJARATI
  0x0b66, // ORIYA
  0x0be6, // TAMIL
  0x0c66, // TELUGU
  0x0ce6, // KANNADA
  0x0d66, // MALAYALAM
  0x0de6, // SINHALA LITH
  0x0e50, // THAI
  0x0ed0, // LAO
  0x0f20, // TIBETAN
  0x1040, // MYANMAR
  0x1090, // MYANMAR SHAN
  0x17e0, // KHMER
  0x1810, // MONGOLIAN
  0x1946, // LIMBU
  0x19d0, // NEW TAI LUE
  0x1a80, // TAI THAM HORA
  0x1a90, // TAI THAM THAM
  0x1b50, // BALINESE
  0x1bb0, // SUNDANESE
  0x1c40, // LEPCHA
  0x1c50, // OL CHIKI
  0xa620, // VAI
  0xa8d0, // SAURASHTRA
  0xa900, // KAYAH LI
  0xa9d0, // JAVANESE
  0xa9f0, // MYANMAR TAI LAING
  0xaa50, // CHAM
  0xabf0, // MEETEI MAYEK
  0xff10, // FULLWIDTH
  0x104a0, // OSMANYA
  0x10d30, // HANIFI ROHINGYA
  0x10d40, // GARAY
  0x11066, // BRAHMI
  0x110f0, // SORA SOMPENG
  0x11136, // CHAKMA
  0x111d0, // SHARADA
  0x112f0, // KHUDAWADI
  0x11450, // NEWA
  0x114d0, // TIRHUTA
  0x11650, // MODI
  0x116c0, // TAKRI
  0x116d0, // MYANMAR PAO
  0x116da, // MYANMAR EASTERN PWO KAREN
  0x11730, // AHOM
  0x118e0, // WARANG CITI
  0x11950, // DIVES AKURU
  0x11bf0, // SUNUWAR
  0x11c50, // BHAIKSUKI
  0x11d50, // MASARAM GONDI
  0x11da0, // GUNJALA GONDI
  0x11de0, // TOLONG SIKI
  0x11f50, // KAWI
  0x16130, // GURUNG KHEMA
  0x16a60, // MRO
  0x16ac0, // TANGSA
  0x16b50, // PAHAWH HMONG
  0x16d70, // KIRAT RAI
  0x1ccf0, // OUTLINED
  0x1d7ce, // MATHEMATICAL BOLD
  0x1d7d8, // MATHEMATICAL DOUBLE-STRUCK
  0x1d7e2, // MATHEMATICAL SANS-SERIF
  0x1d7ec, // MATHEMATICAL SANS-SERIF BOLD
  0x1d7f6, // MATHEMATICAL MONOSPACE
  0x1e140, // NYIAKENG PUACHUE HMONG
  0x1e2f0, // WANCHO
  0x1e4f0, // NAG MUNDARI
  0x1e5f1, // OL ONAL
  0x1e950, // ADLAM
  0x1fbf0, // SEGMENTED
];

/**
 * Nd 숫자를 ASCII 로 옮긴다. 숫자가 아닌 코드포인트는 그대로 둔다.
 *
 * **코드포인트 단위**로 순회한다 — astral 숫자(Adlam·Wancho·Osmanya 등)는 surrogate pair 라
 * `charCodeAt` 으로 훑으면 반쪽만 보고 지나친다.
 */
const toAsciiDigits = (text: string): string => {
  let out = "";
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    let mapped = ch;
    for (const zero of DECIMAL_ZEROS) {
      if (code >= zero && code <= zero + 9) {
        mapped = String(code - zero);
        break;
      }
    }
    out += mapped;
  }
  return out;
};

/**
 * 문자(letter) 판정 — Unicode **category L** 전체.
 *
 * ASCII 만 보면 `김철수010`·`다이버031` 같은 한글·CJK 아이디가 밀착 예외를 못 받아 접두로
 * 차단된다. 한국어 서비스에서 그건 실사용 아이디를 통째로 막는 일이다.
 *
 * `\p{L}` 는 `u` 플래그가 필요한데 tsconfig target 이 `es5` 라 **리터럴로 쓰면 빌드가 깨진다**.
 * 동적 `RegExp` 로 만들고, 지원하지 않는 런타임에서는 ASCII 로 안전하게 내려앉는다.
 */
const LETTER = (() => {
  try {
    return new RegExp("\\p{L}", "u");
  } catch {
    // 구형 런타임 — 최소한 ASCII 는 종전대로 동작한다.
    return /[A-Za-z]/;
  }
})();

const isLetter = (ch: string | undefined): boolean => !!ch && LETTER.test(ch);

/**
 * `idx` **바로 앞**의 코드포인트. `text[idx - 1]` 로 읽으면 astral 문자(surrogate pair)의
 * 하위 surrogate 하나만 집어 문자로 인식하지 못한다 — `𐌰0104` 가 아이디 예외를 못 받았다.
 */
const codePointBefore = (text: string, idx: number): string | undefined => {
  if (idx <= 0) return undefined;
  const low = text.charCodeAt(idx - 1);
  if (low >= 0xdc00 && low <= 0xdfff && idx >= 2) {
    const high = text.charCodeAt(idx - 2);
    if (high >= 0xd800 && high <= 0xdbff) return text.slice(idx - 2, idx);
  }
  return text[idx - 1];
};

/** `idx` **에서 시작하는** 코드포인트. 위와 같은 이유로 인덱싱 대신 이 helper 를 쓴다. */
const codePointAt = (text: string, idx: number): string | undefined => {
  if (idx < 0 || idx >= text.length) return undefined;
  const code = text.codePointAt(idx);
  return code === undefined ? undefined : String.fromCodePoint(code);
};

/**
 * 아이디에서 문자와 숫자를 잇는 데 흔히 쓰이는 구분자. 이 세 글자만 인정한다 —
 * 넓히면 전화 표기(`010-1234-5678`)의 구분자와 구별이 사라진다.
 */
const IDENTIFIER_SEPARATORS = new Set(["_", "-", "."]);

/**
 * 아이디 꼬리로 인정하는 숫자군의 **최대 자릿수**. 국번(`010`·`031`)에 한 자리를 더한
 * 길이까지다 — 그 이상은 번호 조각으로 자라날 수 있어 라벨이 있어도 예외를 주지 않는다.
 */
const IDENTIFIER_DIGIT_MAX = 4;

/**
 * Unicode **General_Category = Cf**(format) 전수 구간. 보이지 않지만 문자열에 남는 문자들이라
 * "보이는 글자 수" 계산과 전화번호 판정에서 반드시 걷어내야 한다.
 *
 * 임의 목록으로 두면 조용히 새는 문자가 생긴다 — U+061C(아랍 문자 마크) · U+1D173(무지컬
 * 서식) · U+E0001(언어 태그) 이 실제로 빠져 있었다.
 *
 *   기준: UnicodeData.txt · Unicode **17.0.0**
 *   출처: https://www.unicode.org/Public/UCD/latest/ucd/UnicodeData.txt
 *   조건: field3(General_Category) == "Cf"
 *   결과: **21구간 / 170코드포인트** (astral 7구간)
 *
 * 정규식 `\p{Cf}` 는 `u` 플래그가 필요해 es5 target 에서 리터럴로 못 쓴다 — 구간 표 +
 * 코드포인트 술어로 둔다. 대조 결과는 `.harness/artifacts/.../ucd-cf-verification.md`.
 */
const FORMAT_RANGES: [number, number][] = [
  [0x00ad, 0x00ad],
  [0x0600, 0x0605],
  [0x061c, 0x061c],
  [0x06dd, 0x06dd],
  [0x070f, 0x070f],
  [0x0890, 0x0891],
  [0x08e2, 0x08e2],
  [0x180e, 0x180e],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x2066, 0x206f],
  [0xfeff, 0xfeff],
  [0xfff9, 0xfffb],
  [0x110bd, 0x110bd],
  [0x110cd, 0x110cd],
  [0x13430, 0x1343f],
  [0x1bca0, 0x1bca3],
  [0x1d173, 0x1d17a],
  [0xe0001, 0xe0001],
  [0xe0020, 0xe007f],
];

const isFormatChar = (code: number): boolean => FORMAT_RANGES.some(([lo, hi]) => code >= lo && code <= hi);

/** 보이지 않는 서식 문자를 코드포인트 단위로 제거한다 (astral 포함). */
const stripFormatChars = (text: string): string => {
  let out = "";
  for (const ch of text) {
    if (!isFormatChar(ch.codePointAt(0) ?? 0)) out += ch;
  }
  return out;
};

const matchesKoreanPhone = (digits: string): boolean => KR_PHONE_PATTERNS.some((pattern) => pattern.test(digits));

/**
 * 앞이 문자에 **직접** 밀착했는가 (`hong0104`·`tel0101234`).
 */
const letterAdjacentAt = (text: string, idx: number): boolean => isLetter(codePointBefore(text, idx));

/** 앞이 `_`·`-`·`.` 하나를 사이에 두고 문자인가 (`kim_0104`·`김-0104`). */
const separatorAttachedAt = (text: string, idx: number): boolean => {
  const prev = codePointBefore(text, idx);
  if (prev === undefined || !IDENTIFIER_SEPARATORS.has(prev)) return false;
  return isLetter(codePointBefore(text, idx - prev.length));
};

/** 뒤가 문자이거나 구분자 하나를 건너 문자인가 (`010kim`·`0104_user`). */
const labelFollowsAt = (text: string, endIdx: number): boolean => {
  const after = codePointAt(text, endIdx);
  if (isLetter(after)) return true;
  if (after === undefined || !IDENTIFIER_SEPARATORS.has(after)) return false;
  return isLetter(codePointAt(text, endIdx + after.length));
};

const matchesKoreanPhonePrefix = (digits: string): boolean => digits.length >= PREFIX_MIN_DIGITS && KR_PHONE_PREFIX_PATTERNS.some((pattern) => pattern.test(digits));

/**
 * **아이디 예외를 줄 수 있는 숫자군인가.**
 *
 * 라운드 28 은 문자 라벨이 붙기만 하면 예외를 줬다 — 라벨은 사람이 정하는 것이라
 * `hp-01012`·`연락처-01012345`·`x-070123456` 처럼 **번호 조각에 이름표만 단 입력**이
 * 통째로 열렸다. 라벨 목록으로는 막을 수 없으니 **숫자군 자체의 성질**로 가른다:
 * 4자리 이하는 아이디 꼬리(`kim_0104`·`user.02`)로 보고, 5자리 이상이면서 국번 구조에
 * 맞으면 라벨이 있어도 전화 조각으로 본다. 구조에 맞지 않는 긴 숫자(`kim99999`)는
 * 종전대로 통과한다 — 과차단하지 않는다.
 *
 * **국가번호 변형까지 본다.** 숫자군 자체만 보면 `tel82101234` 의 `82101234` 가 어떤 국내
 * 패턴에도 맞지 않아 예외를 받고, 그 예외가 `continue` 로 후보를 지워 `nationalCandidates`
 * 가 돌기도 전에 판정이 끝났다 — 국제표기 한 줄로 차단이 통째로 우회됐다.
 */
const exemptibleGroup = (digits: string): boolean => digits.length <= IDENTIFIER_DIGIT_MAX || !nationalCandidates(digits).some(matchesKoreanPhonePrefix);

/**
 * 국가번호를 뗀 국내 표기 후보. 국가번호 해석은 **추가 후보**로만 시도하므로
 * `8212345678` 같은 임의 숫자는 어느 후보도 구조에 맞지 않아 통과한다.
 */
const nationalCandidates = (digits: string): string[] => {
  const withoutCountry = (rest: string) => (rest.startsWith("0") ? rest : `0${rest}`);
  const candidates = [digits];

  if (digits.startsWith(`00${KR_COUNTRY_CODE}`)) candidates.push(withoutCountry(digits.slice(4)));
  if (digits.startsWith(KR_COUNTRY_CODE)) candidates.push(withoutCountry(digits.slice(2)));

  return candidates;
};

/**
 * 텍스트의 숫자 흐름에서 전화번호 후보 window 를 만든다.
 *
 * **숫자 그룹의 연속 부분 체인**이 후보다 — `"1) 010-1234-5678"` 은
 * `["1","010","1234","5678"]` 로 쪼개져 `010`+`1234`+`5678` 체인이 후보가 되므로
 * 앞의 목록 마커 `1` 에 오염되지 않는다.
 *
 * 그룹 경계에 앵커를 두는 것이 핵심이다. 숫자열 전체를 임의 위치에서 잘라 훑으면
 * `202608031234` 같은 **하나의 덩어리**에서도 `026080312`(02+7자리) 같은 조각이 나와
 * 과차단된다 — 그룹 경계 앵커는 단일 덩어리를 쪼개지 않는다.
 */
const phoneCandidateWindows = (text: string, { skipLetterAttached }: { skipLetterAttached: boolean }): string[] => {
  const matches = Array.from(text.matchAll(/[0-9]+/g));
  const groups = matches.map((m) => m[0]);
  const windows: string[] = [];

  /**
   * 체인의 **첫 조각** 후보. 보통은 그 숫자군 전체지만, 뒤에 이어지는 그룹이 있는
   * **완성형 판정**에서는 그 숫자군의 suffix 도 후보로 본다.
   *
   * 후보는 숫자 그룹 경계에서만 시작하므로 `9010-1234-5678` 은 첫 조각이 `9010` 이 되어
   * 체인이 `901012345678`(12자리) 이 되고 어떤 패턴에도 맞지 않아 통과했다 — 실제 번호
   * 앞에 숫자 한 자만 붙이면 되는 우회다. `9010` 의 `010` 에서 시작하는 체인도 본다.
   *
   * **접두 판정에는 쓰지 않는다.** 넣으면 `2026-0803-1234` 의 `026`+`0803` 이 서울 접두로
   * 걸려 날짜·주문번호가 과차단된다. **단일 그룹에도 쓰지 않는다** — 번호 전체가 한
   * 덩어리에 있으면 그 덩어리가 이미 후보이고, suffix 를 열면 `202608031234` 같은
   * 타임스탬프가 쪼개져 과차단된다.
   */
  const firstParts = (start: number): string[] => {
    const group = groups[start];
    if (skipLetterAttached || start === groups.length - 1) return [group];
    // 한국 번호는 `0` 또는 `1` 로 시작한다 — 그 외로 시작하는 suffix 는 후보가 될 수 없다.
    const suffixes = [];
    for (let cut = 1; cut < group.length; cut += 1) {
      const tail = group.slice(cut);
      if (tail[0] === "0" || tail[0] === "1") suffixes.push(tail);
    }
    return [group, ...suffixes];
  };

  /**
   * 체인의 **마지막 조각** 후보. 보통은 그 숫자군 전체지만, 앞에 이어지는 그룹이 있는
   * **완성형 판정**에서는 뒤 1~2자리를 잡음으로 보고 잘라낸 것도 후보로 본다.
   *
   * `9010-1234-56789` 는 `010`+`1234`+`56789` = `010123456789`(12자리) 라 어떤 패턴에도
   * 맞지 않고 통과했다 — 번호 뒤에 숫자 한 자만 붙이면 되는 우회다.
   *
   * **최소 4자리를 남긴다.** 더 깎으면 `2026-1234-5678` 의 `5678` 이 `567`·`56` 으로 쪼개져
   * 주문번호가 과차단된다. 접두 판정·첫 그룹에는 쓰지 않는 이유는 `firstParts` 와 같다.
   */
  const lastParts = (start: number, end: number, precedingLength: number): string[] => {
    const group = groups[end];
    if (skipLetterAttached || end === start) return [group];
    // **후보는 최대 15자리다.** 앞부분 길이를 빼서 마지막 조각의 상한을 먼저 구하지 않으면
    // `010-` + 수천 자리 붙여넣기에서 잘라낸 후보를 전부 만들며 O(n^2) slice 가 된다.
    const maxTail = MAX_PHONE_CANDIDATE_DIGITS - precedingLength;
    if (maxTail <= 0) return [];
    // 잘라낼 길이를 1~2 로 묶으면 **잡음 길이를 공격자가 정한다** — `010x1234x5678901`
    // (뒤 3자)이 통과했다. 가입자 번호 최소 유효 길이(4자리)를 남기는 **모든** prefix 를 본다.
    // 자르지 않은 원본은 언제나 후보다 (상한을 넘으면 호출부가 거른다).
    const parts = [group];
    // 잘라낸 후보는 **상한 안쪽부터** 만든다 — 전체 길이에서 내려오면 `010-` + 수천 자리
    // 붙여넣기에서 버려질 후보를 전부 slice 하며 O(n^2) 가 된다.
    const from = Math.min(group.length - 1, maxTail);
    for (let keep = from; keep >= MIN_SUBSCRIBER_DIGITS; keep -= 1) {
      parts.push(group.slice(0, keep));
    }
    return parts;
  };

  for (let start = 0; start < groups.length; start += 1) {
    // 숫자군이 ASCII 영문자에 **바로 붙어** 있으면 로그인아이디 토큰의 일부로 본다
    // (`hong0104` · `buccl010` · `user0101234`). 다만 이 예외는 **접두 판정에만** 쓴다
    // (`skipLetterAttached`) — 완성형 판정까지 풀면 문자 하나만 앞에 붙여
    // `x010-1234-5678` 로 차단을 통째로 우회할 수 있다. 완성된 번호는 어디에 있든
    // 전화번호다.
    const at = matches[start].index ?? 0;
    // 문자에 밀착한 숫자군이 **구분자(문자 아님)로 이어지는 다음 숫자 그룹**을 갖고 있으면
    // 아이디 토큰이 아니라 전화 표기다 — `tel010-1234-` · `phone010-1234`. 라벨 목록을
    // 하드코딩하지 않고 구조로 가른다.
    //
    // 문자로만 분리된 경우(`a010x1234`)는 전화 표기가 아니므로 예외를 유지한다 — threat
    // model 상 모호한 아이디를 과차단하지 않는다. 다음 그룹이 없으면(`hong0104`) 그대로 예외.
    // 문자와 숫자 사이에 흔히 쓰는 구분자(`_`·`-`·`.`)가 하나 끼어도 **아이디 토큰**이다 —
    // `kim_0104`·`김-0104`·`diver.031`. 구분자를 인정하지 않으면 앞 글자가 문자가 아니라
    // 예외가 풀리고, 이런 평범한 로그인 아이디가 전화 접두로 차단된다.
    // 완성형 판정은 애초에 이 예외를 쓰지 않으므로 `kim_010-1234-5678` 은 계속 막힌다.
    const followedByPhoneGroup = (() => {
      if (start + 1 >= groups.length) return false;
      const gapFrom = at + groups[start].length;
      const gap = text.slice(gapFrom, matches[start + 1].index ?? 0);
      return !Array.from(gap).some(isLetter);
    })();
    // **두 라벨 경로에 같은 자릿수 조건을 건다.** 라운드 28 은 구분자 경로에만 걸었고
    // 직접 밀착(`tel0101234`·`phone01012345`·`연락처01012345`)은 "라운드 11 이래의 계약"
    // 이라는 이유로 열려 있었다 — 구분자 하나를 빼는 것만으로 라운드 28 의 차단이 통째로
    // 우회된다. 라벨은 사람이 정하는 것이라 목록으로 막을 수 없고, 라벨이 앞에 있다는
    // 사실은 뒤 숫자가 아이디 꼬리라는 증거가 되지 못한다. 5자리 이상이면서 국번 구조에
    // 맞는 숫자군은 라벨이 무엇이든 번호 조각이다 (`hong0104`·`buccl010` 은 그대로 통과).
    const labelExempt = (letterAdjacentAt(text, at) || separatorAttachedAt(text, at)) && exemptibleGroup(groups[start]);
    if (skipLetterAttached && labelExempt && !followedByPhoneGroup) continue;

    for (const firstPart of firstParts(start)) {
      let chained = "";
      for (let end = start; end < groups.length; end += 1) {
        // 두 숫자군 사이에 ASCII 문자가 있으면 잇지 않는다 — **접두 판정에서만**.
        // `010kim0104` 를 `0100104` 로 이어 붙이면 존재하지 않는 번호를 만들어 과차단하므로
        // "자라날 수 있는 조각" 판정에는 이 연결을 끊는다.
        //
        // 완성형 판정에는 적용하지 않는다. 적용하면 번호 사이에 문자를 끼우는 것만으로
        // 차단이 통째로 뚫린다 — `010x1234x5678` · `010kim1234kim5678` · `02a123a4567`.
        // 사람이 무엇을 끼워 넣든 그 안에 번호 전체가 들어 있으면 전화번호다.
        if (skipLetterAttached && end > start) {
          const gapFrom = (matches[end - 1].index ?? 0) + groups[end - 1].length;
          const gap = text.slice(gapFrom, matches[end].index ?? 0);
          if (Array.from(gap).some(isLetter)) break;
        }

        // 이 그룹 이전까지 이어 온 체인.
        const base = end === start ? "" : chained;
        // 국제표기 최대 자릿수: `0082` + 국내 11자리 = 15. 가장 짧은 후보(가입자 최소
        // 4자리)로도 넘으면 더 볼 것이 없다. **자르지 않은 원본 길이로 먼저 끊으면**
        // 뒤 잡음이 긴 경우(`010-1234-56789012345`) 잘라낸 후보를 보기도 전에 빠져나간다.
        if (base.length + MIN_SUBSCRIBER_DIGITS > MAX_PHONE_CANDIDATE_DIGITS) break;

        // 체인의 **끝** 숫자군 바로 뒤에 ASCII 문자가 오면 이 숫자열은 더 이상 전화번호로
        // 자라날 수 없다 (`010kim` · `031diver` · `1588team`). "자라날 수 있는 조각" 을
        // 막는 것이 접두 규칙의 목적이므로, 자랄 수 없는 조각은 접두 후보가 아니다.
        // 완성형 판정(`skipLetterAttached: false`)에는 적용하지 않는다 — 완성된 번호는
        // 뒤에 무엇이 오든 전화번호다.
        // 구분자 하나를 건너뛴 뒤의 문자도 종결 경계다 — `0104_user`·`031-diver` 는
        // 뒤에 문자가 있어 더 자랄 수 없는데, 구분자에서 판정을 멈추면 그대로 차단됐다.
        //
        // 여기에도 **같은 자릿수 조건**을 건다. 뒤 라벨 역시 사람이 붙이는 것이라
        // `0101234tel`·`01012345연락처` 처럼 번호 조각에 꼬리표만 단 입력이 종결 경계를
        // 타고 빠져나갔다. `010kim`·`1588team` 같은 4자리 이하는 그대로 통과한다.
        if (skipLetterAttached) {
          const endsAt = (matches[end].index ?? 0) + groups[end].length;
          // 예외 판정은 **이어 온 체인 전체**로 한다. 끝 숫자군만 보면 `010-1234tel` 이
          // 끝 그룹 `1234`(4자리) 로 예외를 받아 `0101234` 조각이 통째로 빠져나갔다.
          if (labelFollowsAt(text, endsAt) && exemptibleGroup(base + groups[end])) continue;
        }

        // 마지막 조각 후보(뒤 잡음을 자른 것)도 함께 본다.
        for (const tail of end === start ? [firstPart] : lastParts(start, end, base.length)) {
          const candidate = base + tail;
          if (candidate.length <= MAX_PHONE_CANDIDATE_DIGITS) windows.push(candidate);
        }

        // 체인 자체는 **자르지 않은 원본**으로 이어 간다 — 잘라 이으면 다음 그룹과의
        // 연결이 어긋난다.
        chained = base + (end === start ? firstPart : groups[end]);
        if (chained.length > MAX_PHONE_CANDIDATE_DIGITS) break;
      }
    }
  }

  return windows;
};

/**
 * 한국 전화번호 구조가 들어 있는가.
 *
 * 자릿수만 보지 않는다 — `12345678`·`202608031234` 같은 숫자형 `user_id`·타임스탬프는
 * 통과시키고, 휴대폰·지역번호·인터넷전화·안심번호·수신자부담·전국대표 번호만 막는다.
 */
/** 후보 window 를 국가번호 변형까지 펼쳐 술어에 태운다. */
const anyCandidateMatches = (value: string, predicate: (digits: string) => boolean, options: { skipLetterAttached: boolean }): boolean => {
  if (typeof value !== "string") return false;

  // NFKC 로 전각을 접고, 눈에 보이지 않는 서식 문자를 지운 뒤 판정한다.
  const normalized = toAsciiDigits(value.normalize("NFKC"));

  return phoneCandidateWindows(normalized, options).some((window) => nationalCandidates(window).some(predicate));
};

/** 완성된 한국 전화번호가 들어 있는가. */
// 완성형은 밀착 예외를 쓰지 않는다 — `x010-1234-5678` 도 전화번호다.
export const looksLikePhoneNumber = (value: string): boolean => anyCandidateMatches(value, matchesKoreanPhone, { skipLetterAttached: false });

/**
 * 한국 전화번호로 자라날 수 있는 조각이 들어 있는가 (완성형 포함).
 *
 * 자동검색이 완성 전에 요청을 만들어 부분 번호를 흘리는 것을 막는다.
 */
// 접두 판정만 밀착 예외를 쓴다 — `buccl010` 의 `010` 은 자라날 조각이 아니라 아이디의 일부다.
// 이 지점이 아이디 과차단을 막는 유일한 완화다.
/**
 * **완성된** 한국 전화번호인가 (부분·접두 아님). path route 가 정수 PK 를 허용하되
 * `15881234` 같은 완성형은 제외해야 해서 따로 노출한다.
 */
export const isCompleteKoreanPhoneNumber = (value: string): boolean => looksLikePhoneNumber(value);

export const looksLikePhoneNumberPrefix = (value: string): boolean => anyCandidateMatches(value, matchesKoreanPhonePrefix, { skipLetterAttached: true });

/** 숫자군에 사람이 붙인 라벨(문자 밀착·구분자 경유, 앞뒤 무관)이 하나라도 있는가. */
const hasDigitLabel = (text: string): boolean =>
  Array.from(text.matchAll(/[0-9]+/g)).some((m) => {
    const at = m.index ?? 0;
    return letterAdjacentAt(text, at) || separatorAttachedAt(text, at) || labelFollowsAt(text, at + m[0].length);
  });

/**
 * **라벨이 붙은 번호 조각**이 들어 있는가 (`tel0101234`·`tel010-1234`·`010-1234tel`).
 *
 * 신뢰 생산자 경로(navigate state `legacyUserId`)는 접두 규칙 전체를 적용할 수 없다 —
 * `0701`·`2026-08-03`·`kim__0104` 같은 정상 `user_id` 의 회원을 영영 열 수 없게 된다.
 * 그렇다고 라벨 붙은 번호 조각까지 열어 두면 로그인 아이디를 URL 밖으로 옮긴 의미가
 * 없어진다: 그 값은 결국 `GET ?search=` 로 나가 access log 에 남는다.
 *
 * 그래서 **라벨의 존재를 게이트로 삼아** 접두 규칙을 그대로 태운다. 판정 자체는
 * `looksLikePhoneNumberPrefix` 와 완전히 같은 코드라 두 경로의 차단 범위가 갈리지 않는다 —
 * 조각을 이어 붙이는 체인·국가번호 변형·유니코드 숫자가 여기서만 새는 일이 없다.
 * 라벨이 아예 없는 값(`0701`)만 이 판정을 통과한다.
 */
export const looksLikeLabeledPhoneFragment = (value: string): boolean => {
  if (typeof value !== "string") return false;
  if (!hasDigitLabel(toAsciiDigits(value.normalize("NFKC")))) return false;
  return looksLikePhoneNumberPrefix(value);
};

export type MemberSearchDecision =
  | { kind: "idle" } // 입력 없음 — 정상 초기 상태 (목록은 전체 조회)
  | { kind: "invalid-empty" } // 서식 문자만 있어 **보이는 글자가 없다** — 요청하지 않음
  | { kind: "too-long" } // 상한 초과 — 분석 전에 끊는다
  | { kind: "too-short" } // 최소 길이 미만 — 요청하지 않음
  | { kind: "phone-blocked" } // 전화번호 형태 — 요청하지 않음
  | { kind: "search"; term: string }; // 서버 검색 가능

/**
 * **판정용 분석 복사본.** NFC 로 모으고 보이지 않는 서식 문자를 지운다.
 *
 * 이 값은 **전송하지 않는다.** BE `user_id` 는 opaque `CharField` 라 저장된 바이트 그대로가
 * 식별자다 — NFD 로 저장된 아이디나 서식 문자를 포함한 아이디를 FE 가 임의로 접어 보내면
 * BE 가 못 찾는다. 정규화는 "전화번호인가 / 보이는 글자가 몇 자인가" 를 **판단하기 위한
 * 분석**이지 값의 표준화가 아니다.
 *
 * 서식 문자만 있는 입력은 여기서 빈 문자열이 되어 요청을 만들지 않는다.
 */
const analysisCopy = (raw: string): string => stripFormatChars((raw ?? "").normalize("NFC")).trim();

/** 전송·정확 일치에 쓰는 값 — 앞뒤 공백만 다듬고 나머지는 **원문 그대로**. */
const transportTerm = (raw: string): string => (raw ?? "").trim();

/**
 * 원문에 **서식 문자(Cf)** 가 하나라도 있는가.
 *
 * `String.trim()` 은 U+FEFF(BOM)를 공백으로 보고 **지운다**. 그래서 BOM 만 붙여넣은 입력이
 * `trim()` 뒤에 빈 문자열이 되어 "입력 없음(idle)" 으로 떨어지고, idle 은 **필터 없는
 * 전체 조회** 경로다 — 관리자는 무언가 검색했다고 믿는데 전 회원이 뜬다.
 *
 * "사용자가 비어 있지 않은 값을 제출했다" 는 사실은 원문의 Cf 존재로만 알 수 있다.
 * 판정은 codepoint 표(`isFormatChar`)로 한다 — 전역 `g` 정규식을 쓰면 `lastIndex` 가
 * 호출 사이에 남아 같은 값을 두 번 물었을 때 답이 달라진다.
 */
const containsFormatChar = (raw: string): boolean => {
  const value = raw ?? "";
  for (let i = 0; i < value.length; ) {
    const code = value.codePointAt(i) ?? 0;
    if (isFormatChar(code)) return true;
    i += code > 0xffff ? 2 : 1;
  }
  return false;
};

/** 사람이 세는 글자 수. `String.length` 는 astral 문자를 2로 세어 emoji 1자를 2자로 본다. */
const codePointLength = (value: string): number => Array.from(value).length;

export const decideMemberSearch = (raw: string): MemberSearchDecision => {
  // **분석 전에** 끊는다 — 상한을 넘는 입력은 어차피 요청하지 않으므로 전화 분석을
  // 돌릴 이유가 없고, 돌리면 붙여넣기 길이에 비례해 UI 가 멈춘다.
  if (exceedsMaxLength(raw ?? "")) return { kind: "too-long" };
  const analysed = analysisCopy(raw);
  const term = transportTerm(raw);
  // "입력 없음"(정상 초기 상태)과 "서식 문자만 있음"(잘못된 입력)을 구분한다. 뭉치면
  // 목록이 후자에서 **필터 없는 전체 조회**를 해버린다.
  // 원문에 서식 문자가 있으면 **비어 있지 않은 제출**이다 — `trim()` 이 BOM 을 지워
  // 빈 문자열이 되더라도 idle(전체 조회)로 떨어뜨리지 않는다.
  if (!term) return containsFormatChar(raw) ? { kind: "invalid-empty" } : { kind: "idle" };
  if (!analysed) return { kind: "invalid-empty" };
  // **UUID 를 가장 먼저 확정한다.** `public_id` 와 UUID 형 `user_id` 는 형식이 확정적인데,
  // 전화 판정을 앞에 두면 숫자 구간이 번호 구조와 겹치는 값(`01012345-6789-…`·
  // `15881588-1600-…`)이 phone-blocked 로 떨어져 정상 회원을 검색조차 못 한다.
  // 딥링크(`?id=`)는 라운드 25 에서 같은 이유로 고쳤다 — 검색창도 같은 계약을 따른다.
  // 전송은 **원문**이다 (opaque `user_id` 보존).
  if (isPublicId(analysed)) return { kind: "search", term };
  // 전화번호 판정이 최소 길이보다 앞선다 — 안내 문구가 달라야 한다.
  // 완성 전 조각까지 막는다 (접두 판정이 완성형을 포함한다).
  // 완성형은 밀착 예외 없이, 접두는 밀착 예외를 두고 본다. 둘 중 하나라도 걸리면 차단.
  if (looksLikePhoneNumber(analysed) || looksLikePhoneNumberPrefix(analysed)) return { kind: "phone-blocked" };
  if (codePointLength(analysed) < MEMBER_SEARCH_MIN_LENGTH) return { kind: "too-short" };
  // **원문**을 보낸다 — 위 판정은 분석 복사본으로 끝났다.
  return { kind: "search", term };
};

// ── 딥링크(`?id=`) 판정 ────────────────────────────────────────────
//
// URL 의 `?id=` 는 사람이 검색창에 친 자유검색과 **다른 의도**다. "이 회원을 보여달라"는
// 식별자 조회이므로 자유검색용 최소 길이 가드를 적용하면 안 된다 — BE `user_id` 는
// `CharField(max_length=40)` 로 최소 길이 제약이 없어서, 1자 ID 회원의 딥링크가
// `too-short` 로 막혀 빈 화면이 됐다.
//
// 값이 `public_id`(UUID) 면 목록을 아예 거치지 않는다. 부분일치 검색 1페이지(50건) 밖으로
// 밀려 대상을 못 찾는 경로가 이 분기에서 통째로 사라진다.
//
// 전화번호 차단은 그대로다 — 딥링크라고 해서 검색어가 access log 에 남는 성질이 달라지지 않는다.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Django `AutoField` 정수 PK 인가. BE 상세는 `public_id` 와 정수 PK 를 모두 받는다.
 *
 * **정규 10진수만** PK 로 본다 — 선행 0(`0158`)·부호(`+1`·`-1`)·소수·지수 표기는 아니고,
 * 32-bit 범위(`1..2147483647`)를 벗어나면 PK 가 될 수 없다. 이 좁은 조건이라야
 * `01012345678` 같은 전화번호를 PK 로 오인하지 않는다.
 */
export const LEGACY_PK_MAX = 2147483647;

export const isLegacyPk = (value: string): boolean => {
  const v = (value ?? "").trim();
  if (!/^[1-9][0-9]*$/.test(v)) return false;
  const n = Number(v);
  return Number.isSafeInteger(n) && n >= 1 && n <= LEGACY_PK_MAX;
};

export const isPublicId = (value: string): boolean => UUID_PATTERN.test((value ?? "").trim());

export type MemberDeepLinkDecision =
  | { kind: "idle" } // `?id=` 없음 — 전체 목록 조회 (기존 초기 로드 경로)
  | { kind: "invalid-empty" } // 서식 문자만 — 요청하지 않음
  | { kind: "too-long" } // 상한 초과 — 분석 전에 끊는다
  | { kind: "invalid-identifier" } // 명시된 계약의 형식을 어긴 값 — 요청하지 않음
  | { kind: "detail"; publicId: string } // UUID — 검색 없이 상세로
  | { kind: "phone-blocked" } // 전화번호 — 요청하지 않음
  // 종류 미표시 UUID — `public_id` 인지 `user_id` 인지 알 수 없다. **term 이 없다**:
  // 어느 해석으로도 조회하지 않는다 (아래 `decideLegacyIdentifierDeepLink` 주석 참조).
  | { kind: "ambiguous-blocked" }
  | { kind: "lookup"; term: string }; // legacy user_id — 검색으로 정확 일치를 찾는다

export const decideMemberDeepLink = (raw: string): MemberDeepLinkDecision => {
  if (exceedsMaxLength(raw ?? "")) return { kind: "too-long" };
  const analysed = analysisCopy(raw);
  const term = transportTerm(raw);
  if (!term) return containsFormatChar(raw) ? { kind: "invalid-empty" } : { kind: "idle" };
  if (!analysed) return { kind: "invalid-empty" };
  // UUID 는 대소문자를 가리지 않는 표기다 — 경로를 하나로 모으기 위해 소문자로 정규화한다.
  if (isPublicId(analysed)) return { kind: "detail", publicId: analysed.toLowerCase() };
  // 완성형은 밀착 예외 없이, 접두는 밀착 예외를 두고 본다. 둘 중 하나라도 걸리면 차단.
  if (looksLikePhoneNumber(analysed) || looksLikePhoneNumberPrefix(analysed)) return { kind: "phone-blocked" };
  // legacy 조회도 **원문**으로 한다 — opaque `user_id` 를 접으면 못 찾는다.
  return { kind: "lookup", term };
};

/** 세 회원 검색 UI 가 공유하는 문구 — 안내와 실제 검색 범위를 한곳에서 맞춘다. */
export const MEMBER_SEARCH_LABEL = "회원 검색 (회원명 / 아이디)";
// 전화번호뿐 아니라 `0803` 같은 **숫자 식별자**도 같은 판정에 걸린다 — 왜 막혔는지
// 정확히 설명해야 관리자가 "그런 회원이 없다" 로 오해하지 않는다.
export const MEMBER_SEARCH_PHONE_NOTICE = "개인정보 보호를 위해 전화번호 형태 또는 숫자 식별자는 검색할 수 없습니다. 회원명 또는 아이디로 검색하세요.";
/**
 * `?id=` **legacy `user_id`** 딥링크 판정.
 *
 * path(`/user-list/:id`)는 DB PK 또는 `public_id` 지만 query 는 내부 생산자가 싣는
 * `user_id` 다 — 계약이 다르므로 판정도 따로 둔다. 여기서 **숫자 PK 로 직접 이동하지
 * 않는다**: `user_id` 가 `12345678` 인 회원과 PK 12345678 이 서로 다른 사람일 수 있어,
 * 직접 라우팅하면 엉뚱한 회원의 상세가 열리고 mutation 대상까지 바뀐다.
 *
 * `1588`·`1600`·`1800` 같은 **4자리 이하 양수**는 전국대표번호 *접두* 로 걸려 조회조차
 * 못 했다 — 그 자체로는 전화번호가 아니므로 접두 예외로 lookup 을 허용한다.
 * 완성형(`15881234`)은 그대로 차단한다.
 */
const SHORT_POSITIVE_NUMBER = /^[1-9][0-9]{0,3}$/;

export const decideLegacyIdentifierDeepLink = (raw: string): MemberDeepLinkDecision => {
  if (exceedsMaxLength(raw ?? "")) return { kind: "too-long" };
  const term = transportTerm(raw);
  const analysed = analysisCopy(raw);
  if (!term) return containsFormatChar(raw) ? { kind: "invalid-empty" } : { kind: "idle" };
  if (!analysed) return { kind: "invalid-empty" };

  // **UUID 는 종류를 알 수 없는 값이다.** 형식만으로 `public_id` 라고 단정하면, `user_id`
  // 가 UUID 모양인 회원 A 를 가리킨 링크가 같은 문자열을 `public_id` 로 가진 **다른 회원 B**
  // 의 상세로 간다 — 열람 대상과 mutation 대상이 통째로 바뀐다.
  //
  // 검색으로 두 해석을 대조해 가려내는 것도 **불가능하다**: BE `search` 는 name/user_id 만
  // `icontains` 필터하므로 `public_id` 가 일치하는 행은 애초에 응답에 오지 않는다. 충돌
  // 감지는 구조적으로 항상 실패하고, 남는 것은 한쪽 해석으로 **조용히 이동**하는 동작뿐이다.
  // 그래서 여기서 끊는다 — 요청도 만들지 않고(값이 access log 에 남지 않는다) 안내한다.
  // 종류를 아는 생산자는 `?public_id=`(typed query) 또는 navigate state 의 `legacyUserId` 를 쓴다.
  if (isPublicId(analysed)) return { kind: "ambiguous-blocked" };

  // 완성형은 무조건 차단 — 짧은 숫자 예외보다 앞선다.
  if (looksLikePhoneNumber(analysed)) return { kind: "phone-blocked" };
  if (SHORT_POSITIVE_NUMBER.test(analysed)) return { kind: "lookup", term };

  // 나머지는 기존 판정 그대로다 — 종류 미표시 링크의 자유 입력형 차단(접두 규칙 포함)은
  // 그대로 유지한다.
  const fallback = decideMemberDeepLink(raw);
  return fallback.kind === "detail" ? { kind: "ambiguous-blocked" } : fallback;
};

// ── 명시된 producer→consumer 딥링크 계약 ─────────────────────────────
//
// **URL 로 나르는 식별자는 `public_id` 하나뿐이다.**
//
//  - `?public_id=<UUID>` : 외부 노출 식별자. 검색 없이 상세로 직행한다.
//  - `?id=<...>`         : 종류 미표시 구 링크. 확정할 수 없으면 조회하지 않고 끊는다.
//
// 로그인 아이디(`user_id`)를 싣는 query 계약은 **없다**. 한때 `?user_id=` 가 있었지만,
// 생산자가 쓰지 않게 된 뒤에도 소비자에 남겨 두면 수기·북마크 URL 의 값이 그대로
// `?search=` 로 나가 access log 에 남고 주소창·히스토리·Referer 에도 잔류한다.
// legacy `user_id` 는 아래 navigate state 계약으로만 지원한다.
export const MEMBER_DEEP_LINK_PARAM_PUBLIC_ID = "public_id";
export const MEMBER_DEEP_LINK_PARAM_LEGACY = "id";

/**
 * `public_id` 가 없는 구 데이터의 legacy `user_id` 는 **URL 에 싣지 않는다.**
 *
 * URL query 로 나르면 로그인 아이디가 주소창·브라우저 히스토리·이 화면에서 나가는 모든
 * 요청의 Referer 에 남는다 — 그래서 query 계약을 아예 두지 않는다. React Router 의 navigation state 는 히스토리 entry 에만 붙고
 * URL 에 나타나지 않으므로, 종류를 아는 생산자는 이 키로 명시해 넘긴다.
 *
 * 새로고침하면 state 는 사라진다 — 그때 URL 에는 아무 단서도 없으므로 소비자는
 * **추측하지 않고** 평소의 전체 목록으로 돌아간다. 잃어버릴 수 있다는 것이 이 계약의 값이다.
 */
export const MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID = "legacyUserId";

/** 외부에서 온 state 는 믿지 않는다 — 문자열일 때만 식별자로 쓴다. */
export const readLegacyUserIdState = (state: unknown): string => {
  const value = (state as Record<string, unknown> | null)?.[MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID];
  return typeof value === "string" ? value : "";
};

/**
 * `?public_id=` — **UUID 만** 유효한 계약이다.
 *
 * 형식을 어긴 값은 검색으로 흘리지 않는다. 생산자가 종류를 명시했는데 값이 그 종류가
 * 아니면 그것은 잘못된 링크이지 "다른 뜻일 수도 있는 값" 이 아니다 — 추측해서 요청을
 * 만들면 그 값이 access log 에 남는다.
 */
export const decideTrustedPublicIdDeepLink = (raw: string): MemberDeepLinkDecision => {
  if (exceedsMaxLength(raw ?? "")) return { kind: "too-long" };
  const term = transportTerm(raw);
  const analysed = analysisCopy(raw);
  if (!term) return containsFormatChar(raw) ? { kind: "invalid-empty" } : { kind: "idle" };
  if (!analysed) return { kind: "invalid-empty" };
  if (isPublicId(analysed)) return { kind: "detail", publicId: analysed.toLowerCase() };
  return { kind: "invalid-identifier" };
};

/**
 * navigate state `legacyUserId` — **로그인 아이디**임이 명시된 계약이다.
 *
 * URL 이 아니라 history entry 의 state 로만 들어온다 (`MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID`).
 * UUID 모양도 `user_id` 이므로 상세로 직행하지 않고 정확 일치를 찾는다.
 *
 * 전화번호 차단은 **완성형만** 남긴다. 접두 규칙은 사람이 검색창에 치는 자유 입력이
 * 번호를 완성하기 전에 흘리는 것을 막는 장치인데, 이 경로는 사람이 타이핑하는 값이 아니라
 * 앱 내부 생산자가 실재하는 `user_id` 를 실은 값이다. 접두 규칙을 그대로 적용하면
 * `0701`·`2026-08-03`·`kim__0104` 같은 **정상 아이디의 회원을 영영 열 수 없다**.
 * 완성된 번호는 어떤 계약에서도 검색어로 나가지 않는다.
 *
 * **라벨이 붙은 번호 조각은 예외다** (`tel0101234`·`01012345연락처`). 접두 규칙 전체와 달리
 * 이 판정은 라벨을 신호로 삼아 정상 아이디를 건드리지 않으면서, 로그인 아이디를 URL 밖으로
 * 옮긴 변경이 `?search=` 로 되돌아가는 경로를 닫는다.
 */
export const decideTrustedUserIdDeepLink = (raw: string): MemberDeepLinkDecision => {
  if (exceedsMaxLength(raw ?? "")) return { kind: "too-long" };
  const term = transportTerm(raw);
  const analysed = analysisCopy(raw);
  if (!term) return containsFormatChar(raw) ? { kind: "invalid-empty" } : { kind: "idle" };
  if (!analysed) return { kind: "invalid-empty" };
  // **UUID 를 가장 먼저 확정한다.** `decideMemberSearch` 와 같은 이유다 — 숫자 구간이 번호
  // 구조와 겹치는 UUID 형 `user_id`(`01012345-abcd-…`·`15881234-…`)가 전화 판정에 걸려
  // 정상 회원을 열 수 없었다. 형식이 확정적인 값에 확률적 판정을 태우지 않는다.
  // 상세로 직행하지 않고 **lookup** 인 것은 그대로다 — 이 계약의 값은 `user_id` 다.
  if (isPublicId(analysed)) return { kind: "lookup", term };
  if (looksLikePhoneNumber(analysed) || looksLikeLabeledPhoneFragment(analysed)) return { kind: "phone-blocked" };
  // 조회도 **원문**으로 한다 — opaque `user_id` 를 접으면 BE 가 못 찾는다.
  return { kind: "lookup", term };
};

export const MEMBER_SEARCH_TOO_LONG_NOTICE = `검색어는 ${MEMBER_SEARCH_MAX_INPUT_LENGTH}자 이하로 입력하세요.`;
export const MEMBER_SEARCH_INVALID_NOTICE = "보이는 글자가 없습니다. 회원명 또는 아이디를 입력하세요.";
// page N 요청이 구 배열 응답으로 정규화돼 1페이지로 돌아갔을 때.
export const MEMBER_LIST_LEGACY_RESET_NOTICE = "이전 형식 응답이라 1페이지로 돌아갔습니다.";
export const MEMBER_SEARCH_TOO_SHORT_NOTICE = `검색어를 ${MEMBER_SEARCH_MIN_LENGTH}자 이상 입력하세요.`;
// 딥링크가 부분일치 1페이지 안에서 정확 일치를 못 찾았을 때. 조용히 빈 화면을 두지 않고,
// 지금 보이는 것이 "부분일치 일부"라는 사실을 관리자에게 알린다.
// 상세는 `public_id` 로만 연다 — 응답에 그 값이 없으면 열 수 없다는 사실을 알린다.
export const MEMBER_DETAIL_UNAVAILABLE_NOTICE = "이 회원은 식별자가 없어 상세를 열 수 없습니다.";
// 종류 미표시 링크의 값이 어느 회원을 가리키는지 **확정할 수 없을 때**. 하나를 골라 열면
// 엉뚱한 회원의 개인정보를 보여주게 되므로, 조회도 하지 않고 사실만 알린다.
// (조회를 만들지 않으므로 "아래 목록" 이 없다 — 직접 검색을 안내한다.)
export const MEMBER_DEEP_LINK_AMBIGUOUS_NOTICE = "이 링크에는 회원 식별자 종류가 없어 어느 회원인지 확정할 수 없습니다. 회원명 또는 아이디로 검색하세요.";
// `?public_id=` 처럼 종류가 명시됐는데 값이 그 형식이 아닐 때 — 잘못된 링크다.
export const MEMBER_DEEP_LINK_INVALID_ID_NOTICE = "회원 식별자 형식이 올바르지 않습니다.";
export const MEMBER_DEEP_LINK_NOT_FOUND_NOTICE = "요청한 회원을 이 페이지에서 찾지 못했습니다. 아래 부분일치 결과에서 확인하세요.";
